import { useMemo, useRef, useState } from 'react';
import { useQuery } from '../hooks/useQuery';
import {
  api, CatchupCandidate, CatchupRunResult, HarvestImportResult, InvoiceDetailBackfillResult,
  RefreshRun, RefreshWarning,
} from '../lib/api';
import {
  SectionLabel, Card, CardHeader, MetricCard, Badge, ActionButton, Spinner, EmptyState,
  Table, Th, Tr, Td,
} from '../components/ui';

function statusBadge(status: RefreshRun['status']) {
  const map: Record<RefreshRun['status'], 'green' | 'amber' | 'red' | 'blue'> = {
    success: 'green', warning: 'amber', failed: 'red', running: 'blue',
  };
  return <Badge variant={map[status]}>{status}</Badge>;
}

function fmtDuration(start: string, end: string | null): string {
  if (!end) return 'running…';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function fmtDatetime(iso: string): string {
  // Browser-local timezone: operators reading the Refresh page think in
  // their local clock, not UTC. `timeZoneName: 'short'` shows e.g. "EDT"
  // so it's obvious what zone we're displaying.
  return new Date(iso).toLocaleString(undefined, {
    month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
    timeZoneName: 'short',
  });
}

// Human labels for known warning categories. Unknown categories render verbatim.
const CATEGORY_LABELS: Record<string, string> = {
  missing_customer:       'ChargeOver: missing customer',
  missing_item:           'ChargeOver: missing item',
  missing_billing_item:   'ChargeOver: customer has no billing item',
  missing_rate:           'ChargeOver: item has no rate',
  fixed_fee:              'Fixed-fee contract',
  chargeover_unresolved:  'ChargeOver: unresolved',
  chargeover_fetch_failed:'ChargeOver: fetch failed',
  chargeover_unconfigured:'ChargeOver: not configured',
  orphan_worklog:         'Orphan worklog (unknown issue)',
  fatal:                  'Fatal error',
};

function categoryLabel(c: string): string {
  return CATEGORY_LABELS[c] ?? c;
}

export default function AdminRefreshPage() {
  const { data, isLoading, error, refetch } = useQuery(() => api.admin.refreshRuns.list());
  const { data: wbSummary, refetch: refetchWb } = useQuery(() => api.admin.writebacks.summary());
  const [triggering, setTriggering] = useState(false);
  const [draining, setDraining] = useState(false);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  async function drainWritebacks() {
    setDraining(true);
    try {
      const r = await api.admin.writebacks.drain();
      alert(`Writeback drain: ${r.succeeded} done, ${r.failed} failed, ${r.skipped_unconfigured} skipped (no field IDs configured)`);
      refetchWb();
    } catch (e) {
      alert(`Drain failed: ${(e as Error).message}`);
    } finally {
      setDraining(false);
    }
  }

  async function triggerRefresh() {
    setTriggering(true);
    try {
      await api.admin.refreshRuns.trigger();
      refetch();
    } catch (e) {
      alert(`Failed to trigger refresh: ${(e as Error).message}`);
    } finally {
      setTriggering(false);
    }
  }

  const runs = data?.runs ?? [];
  const lastSuccess = runs.find((r) => r.status === 'success');

  return (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        <MetricCard
          label="Last data sync"
          value={lastSuccess ? fmtDatetime(lastSuccess.started_at) : '—'}
          sub={lastSuccess ? `${lastSuccess.deliverables_processed} deliverables · ${lastSuccess.worklogs_processed} worklogs` : undefined}
        />
        <MetricCard label="Next scheduled sync" value="Hourly" sub="Runs at :00 every hour (UTC)" />
        <MetricCard label="Total runs (last 30)" value={runs.length} sub={`${runs.filter((r) => r.status === 'failed').length} failed`} />
      </div>

      {wbSummary && (wbSummary.pending > 0 || wbSummary.done_today > 0) && (
        <WritebacksCard
          summary={wbSummary}
          draining={draining}
          onDrain={drainWritebacks}
        />
      )}

      <WebhookCatchupCard />

      <DepositFlagsCard />

      <InvoiceDetailBackfillCard />

      <HarvestImportCard onComplete={() => { refetch(); }} />

      <div>
        <SectionLabel>Sync log</SectionLabel>
        <Card>
          <CardHeader>
            <span style={{ fontSize: 12, fontWeight: 500 }}>Recent sync runs</span>
            <ActionButton onClick={triggerRefresh} variant="primary">
              {triggering ? 'Triggering…' : 'Trigger manual sync'}
            </ActionButton>
          </CardHeader>

          {isLoading ? <Spinner /> : error ? <EmptyState message={error} /> : !runs.length ? (
            <EmptyState message="No refresh runs found." />
          ) : (
            <div>
              {runs.map((r) => {
                const hasDetails = (r.status === 'warning' || r.status === 'failed') && !!r.error_message;
                const expanded = expandedRunId === r.id;
                return (
                  <div key={r.id} style={{ borderBottom: '0.5px solid var(--color-border)' }}>
                    <div
                      onClick={() => hasDetails && setExpandedRunId(expanded ? null : r.id)}
                      style={{
                        padding: '10px 16px',
                        display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap',
                        cursor: hasDetails ? 'pointer' : 'default',
                        background: expanded ? 'var(--color-bg)' : undefined,
                      }}
                    >
                      <div style={{ flexShrink: 0 }}>{statusBadge(r.status)}</div>
                      <div style={{ flexShrink: 0, display: 'flex', gap: 4 }}>
                        <Badge variant="gray">{r.triggered_by}</Badge>
                        <Badge variant={r.is_full_sync ? 'blue' : 'green'}>
                          {r.is_full_sync ? 'full' : 'incremental'}
                        </Badge>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--color-text-muted)', minWidth: 160, flexShrink: 0 }}>
                        {fmtDatetime(r.started_at)}
                      </div>
                      {r.status !== 'failed' && r.status !== 'running' && (
                        <div style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>
                          {r.deliverables_processed} deliverables · {r.worklogs_processed} worklogs · {fmtDuration(r.started_at, r.ended_at)}
                        </div>
                      )}
                      {r.error_message && (
                        <div style={{ fontSize: 11, color: r.status === 'warning' ? 'var(--color-amber-text)' : 'var(--color-red-text)', flex: 1 }}>
                          {r.error_message}
                          {hasDetails && (
                            <span style={{ color: 'var(--color-blue)', marginLeft: 8 }}>
                              {expanded ? '(hide details ↑)' : '(show details ↓)'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                    {hasDetails && expanded && <WarningDetails runId={r.id} />}
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

function WarningDetails({ runId }: { runId: string }) {
  const { data, isLoading, error } = useQuery(() => api.admin.refreshRuns.warnings(runId), [runId]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [search, setSearch] = useState('');

  const warnings: RefreshWarning[] = data?.warnings ?? [];

  const counts = useMemo(() => {
    const m = new Map<string, number>();
    for (const w of warnings) m.set(w.category, (m.get(w.category) ?? 0) + 1);
    return m;
  }, [warnings]);

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return warnings.filter((w) => {
      if (categoryFilter !== 'all' && w.category !== categoryFilter) return false;
      if (!needle) return true;
      return (w.subject ?? '').toLowerCase().includes(needle)
        || w.message.toLowerCase().includes(needle)
        || w.category.toLowerCase().includes(needle);
    });
  }, [warnings, categoryFilter, search]);

  if (isLoading) return <div style={{ padding: '12px 16px' }}><Spinner /></div>;
  if (error) return <div style={{ padding: '12px 16px' }}><EmptyState message={error} /></div>;
  if (warnings.length === 0) {
    return <div style={{ padding: '12px 16px', fontSize: 11, color: 'var(--color-text-muted)' }}>No structured warnings recorded for this run (older runs only store the summary).</div>;
  }

  return (
    <div style={{ padding: '10px 16px 16px', background: 'var(--color-bg)' }}>
      <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 8 }}>
        <CategoryChip active={categoryFilter === 'all'} onClick={() => setCategoryFilter('all')}>
          All <Badge variant="gray">{warnings.length}</Badge>
        </CategoryChip>
        {Array.from(counts.entries())
          .sort((a, b) => b[1] - a[1])
          .map(([cat, n]) => (
            <CategoryChip key={cat} active={categoryFilter === cat} onClick={() => setCategoryFilter(cat)}>
              {categoryLabel(cat)} <Badge variant="amber">{n}</Badge>
            </CategoryChip>
          ))}
        <span style={{ marginLeft: 'auto' }}>
          <input
            type="search"
            placeholder="Filter by subject or message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              fontSize: 11, padding: '4px 8px', width: 240,
              border: '0.5px solid var(--color-border-strong)',
              borderRadius: 'var(--radius-sm)', background: 'var(--color-surface)',
            }}
          />
        </span>
      </div>
      <div style={{ maxHeight: 360, overflow: 'auto', border: '0.5px solid var(--color-border)', borderRadius: 4, background: 'var(--color-surface)' }}>
        <Table>
          <thead>
            <tr>
              <Th>Category</Th>
              <Th>Subject</Th>
              <Th>Message</Th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((w) => (
              <Tr key={w.id}>
                <Td muted style={{ fontSize: 11, whiteSpace: 'nowrap' }}>{categoryLabel(w.category)}</Td>
                <Td bold>{w.subject ?? '—'}</Td>
                <Td muted style={{ fontSize: 11 }}>{w.message}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      </div>
    </div>
  );
}

function CategoryChip({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        fontSize: 11, padding: '4px 10px',
        borderRadius: 4,
        border: active ? '0.5px solid var(--color-blue)' : '0.5px solid var(--color-border-strong)',
        background: active ? 'var(--color-blue-bg)' : 'var(--color-surface)',
        color: active ? 'var(--color-blue-text)' : 'var(--color-text-muted)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// ─── Harvest CSV upload ─────────────────────────────────────────────────────
// One-time import path for the decommissioned Harvest timesheet export.
// File is posted to /admin/imports/harvest as raw text; the server parses,
// inserts idempotently (harvest-<hash> ids), and rebuilds bonus snapshots
// so the hours surface in shipped-value reports immediately.
function HarvestImportCard({ onComplete }: { onComplete: () => void }) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<HarvestImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function run(dryRun: boolean) {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const text = await file.text();
      const r = await api.admin.importHarvest(text, dryRun);
      setResult(r);
      if (!dryRun) onComplete();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <SectionLabel>Harvest timesheet import</SectionLabel>
      <Card>
        <div style={{ padding: '12px 16px', display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={(e) => { setFile(e.target.files?.[0] ?? null); setResult(null); setError(null); }}
            style={{ fontSize: 12 }}
          />
          <span style={{ flex: 1, fontSize: 11, color: 'var(--color-text-muted)' }}>
            {file
              ? `${file.name} · ${Math.round(file.size / 1024)} kB`
              : 'Select the Harvest CSV export. Idempotent — re-uploading the same file is a no-op.'}
          </span>
          <ActionButton onClick={() => run(true)} variant="default" disabled={!file || busy}>
            {busy ? 'Running…' : 'Dry run'}
          </ActionButton>
          <ActionButton onClick={() => run(false)} variant="primary" disabled={!file || busy}>
            {busy ? 'Importing…' : 'Import'}
          </ActionButton>
        </div>

        {error && (
          <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--color-border)', background: 'var(--color-red-bg)', color: 'var(--color-red-text)', fontSize: 12 }}>
            {error}
          </div>
        )}

        {result && (
          <div style={{ padding: '10px 16px', borderTop: '0.5px solid var(--color-border)', fontSize: 12, display: 'grid', gap: 4 }}>
            <div>
              <Badge variant={result.dry_run ? 'gray' : 'green'}>
                {result.dry_run ? 'Dry run (no writes)' : 'Imported'}
              </Badge>
              {' '}
              Parsed <strong>{result.parsed}</strong> rows
              {result.skipped_bad_shape > 0 && <> · <span style={{ color: 'var(--color-amber)' }}>{result.skipped_bad_shape} skipped (bad shape)</span></>}
            </div>
            <div>
              Users: {result.users_known} already known
              {result.users_placeholder > 0 && <> · {result.users_placeholder} placeholder{result.users_placeholder === 1 ? '' : 's'} created</>}
            </div>
            <div>
              Worklogs: {result.worklogs_already_imported} already imported
              {result.dry_run
                ? null
                : <> · <strong>{result.worklogs_inserted}</strong> newly inserted</>}
            </div>
            {result.missing_deliverables_count > 0 && (
              <div style={{ color: 'var(--color-amber)' }}>
                {result.missing_deliverables_count} deliverable id(s) not yet in the DB — worklogs insert but stay invisible in deliverable-joined reports until a Jira refresh pulls them in.
                {result.missing_deliverables_sample.length > 0 && (
                  <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginTop: 4, fontFamily: 'var(--font-mono, monospace)' }}>
                    {result.missing_deliverables_sample.join(', ')}
                    {result.missing_deliverables_count > result.missing_deliverables_sample.length && <> …</>}
                  </div>
                )}
              </div>
            )}
            {result.bonus_snapshots && (
              <div>
                Bonus snapshots rebuilt: <strong>{result.bonus_snapshots.rows}</strong> rows
                across {result.bonus_snapshots.users} users × {result.bonus_snapshots.months} months
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
}

// ─── Writebacks card ────────────────────────────────────────────────────────

interface WbSummary {
  pending: number;
  done_today: number;
  failed_latest: Array<{ jira_issue_key: string; issue_type: string; last_error: string | null; attempt_count: number }>;
}

/**
 * Writebacks card with a toggle-able detail table. Summary loads on every
 * page render (cheap); the full pending list loads lazily when the
 * operator clicks "View pending" so the default page render stays light.
 */
// ─── Webhook catch-up card ──────────────────────────────────────────────────

/**
 * Invoices whose ChargeOver webhook never arrived.
 *
 * ChargeOver fires each invoice webhook once, on change — so anything that
 * happened while the endpoint was down is never re-sent, and those invoices
 * sit Unapproved (or paid with no deposit credit) indefinitely. The refresh
 * now repairs them at the tail of every run; this card is the operator's view
 * of the same thing: what is outstanding, what a run would do, and a way to
 * push it along without waiting for the nightly.
 *
 * Preview before the first live run. Both actions are customer-visible —
 * approving an invoice issues it, and a deposit credit is a real credit memo —
 * and the very first pass can reach back over the entire invoice history.
 */
function WebhookCatchupCard() {
  const { data, refetch } = useQuery(() => api.admin.webhookCatchup.pending(100));
  const [running, setRunning] = useState<'preview' | 'live' | null>(null);
  const [result, setResult] = useState<CatchupRunResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  const counts = data?.counts;
  const candidates = data?.candidates ?? [];

  async function run(dryRun: boolean) {
    if (!dryRun && !window.confirm(
      'This writes to ChargeOver: unapproved invoices get issued, and paid invoices with a '
      + 'Deposit line get a credit memo minted and applied. Run the preview first if you have not. Continue?',
    )) return;
    setRunning(dryRun ? 'preview' : 'live');
    setError(null);
    try {
      const r = await api.admin.webhookCatchup.run({ limit: 50, dryRun });
      setResult(r);
      setExpanded(true);
      refetch();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(null);
    }
  }

  const unmatched = counts?.unmatched_draft_statuses ?? [];

  // Nothing outstanding and nothing just run: stay out of the way. The refresh
  // handles this unattended, so the card only earns its space when there is
  // something to look at — with one exception, below: draft invoices we can't
  // classify are worth showing even at zero, because a zero that comes from a
  // status label we don't recognise looks exactly like a zero that means
  // "all healthy".
  if (counts && counts.total === 0 && unmatched.length === 0 && !result) return null;

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Webhook catch-up</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
          Invoices ChargeOver never told us about — a webhook that didn’t arrive is never re-sent, so
          these stay unapproved or uncredited until something repairs them. Every sync repairs up to
          50; preview shows exactly what a run would do without writing anything.
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <ActionButton onClick={() => run(true)} variant="default" disabled={running !== null}>
            {running === 'preview' ? 'Checking…' : 'Preview'}
          </ActionButton>
          <ActionButton onClick={() => run(false)} variant="primary" disabled={running !== null}>
            {running === 'live' ? 'Repairing…' : 'Repair 50'}
          </ActionButton>
        </span>
      </CardHeader>

      {counts && (
        <div style={{
          padding: '10px 16px', borderTop: '0.5px solid var(--color-border)',
          fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center',
        }}>
          <span><strong>Awaiting approval:</strong> {counts.auto_approve}</span>
          <span><strong>Awaiting deposit credit:</strong> {counts.deposit_credit}</span>
          {/* These counts come from our own tables, which still hold the
              pre-repair state: an approval only clears once the next sync
              pulls ChargeOver's new status back, and a credit only once the
              memo shows up in the transaction feed. Without this note the row
              reads as "nothing happened" right after a successful run, and
              the obvious response is to run it again. */}
          {result && !result.dry_run && (
            <span style={{ color: 'var(--color-text-muted)' }}>
              — as of the last sync; the {result.approved + result.credited} just repaired clear once the next sync reads ChargeOver back
            </span>
          )}
          {candidates.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              style={{
                marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
                font: 'inherit', color: 'var(--color-text-muted)', cursor: 'pointer',
              }}
            >{expanded ? 'Hide detail' : 'Show detail'}</button>
          )}
        </div>
      )}

      {unmatched.length > 0 && (
        <div style={{
          padding: '10px 16px', background: 'var(--color-amber-bg)', color: 'var(--color-amber-text)',
          fontSize: 11, borderTop: '0.5px solid var(--color-border)',
        }}>
          Not being considered for approval: {unmatched.map((u) => `${u.count} × “${u.raw_status ?? 'no status'}”`).join(', ')}.
          {' '}The approval check looks for ChargeOver’s “Unapproved” label. If the invoices you expect to see
          here are in that list under a different name, that’s the label to match on — say so and it can be widened.
        </div>
      )}

      {error && (
        <div style={{
          padding: '10px 16px', background: 'var(--color-red-bg)',
          color: 'var(--color-red-text)', fontSize: 12, borderTop: '0.5px solid var(--color-border)',
        }}>{error}</div>
      )}

      {result && (
        <div style={{
          padding: '10px 16px', borderTop: '0.5px solid var(--color-border)',
          fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap',
        }}>
          <span style={{ color: 'var(--color-text-muted)' }}>
            <strong>{result.dry_run ? 'Preview' : 'Repaired'}:</strong> {result.scanned} checked
          </span>
          <span style={{ color: 'var(--color-green-text)' }}>
            <strong>{result.dry_run ? 'Would approve' : 'Approved'}:</strong> {result.approved}
          </span>
          <span style={{ color: 'var(--color-green-text)' }}>
            <strong>{result.dry_run ? 'Would credit' : 'Credited'}:</strong> {result.credited}
          </span>
          <span style={{ color: 'var(--color-text-muted)' }}><strong>Skipped:</strong> {result.skipped}</span>
          {result.errored > 0 && (
            <span style={{ color: 'var(--color-red-text)' }}><strong>Errored:</strong> {result.errored}</span>
          )}
          <span style={{ marginLeft: 'auto', color: result.remaining > 0 ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
            <strong>Remaining:</strong> {result.remaining}
            {result.remaining > 0 && ' — run again to continue'}
          </span>
        </div>
      )}

      {expanded && result && result.outcomes.length > 0 && (
        <Table>
          <thead>
            <Tr>
              <Th>Invoice</Th>
              <Th>Customer</Th>
              <Th>Missing</Th>
              <Th>Outcome</Th>
              <Th>Detail</Th>
            </Tr>
          </thead>
          <tbody>
            {result.outcomes.map((o) => (
              <Tr key={`${o.chargeover_invoice_id}-${o.action}`}>
                <Td>{o.invoice_number ?? `#${o.chargeover_invoice_id}`}</Td>
                <Td>{o.customer_name ?? '—'}</Td>
                <Td>{o.action === 'auto_approve' ? 'Approval' : 'Deposit credit'}</Td>
                <Td>
                  <Badge variant={o.result === 'applied' ? 'green' : o.result === 'errored' ? 'red' : 'gray'}>
                    {o.result}
                  </Badge>
                </Td>
                <Td muted>{o.detail}</Td>
              </Tr>
            ))}
          </tbody>
        </Table>
      )}

      {expanded && !result && candidates.length > 0 && (
        <CatchupCandidateTable rows={candidates} />
      )}
    </Card>
  );
}

/** The outstanding set, before anything is run against it. Names and invoice
 *  numbers rather than ChargeOver ids — an operator shouldn't have to look a
 *  row up to know what it is. */
function CatchupCandidateTable({ rows }: { rows: CatchupCandidate[] }) {
  const money = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
  return (
    <Table>
      <thead>
        <Tr>
          <Th>Invoice</Th>
          <Th>Customer</Th>
          <Th>Status</Th>
          <Th>Missing</Th>
          <Th right>Amount</Th>
          <Th>Date</Th>
        </Tr>
      </thead>
      <tbody>
        {rows.map((r) => (
          <Tr key={`${r.chargeover_invoice_id}-${r.action}`}>
            <Td>{r.invoice_number ?? `#${r.chargeover_invoice_id}`}</Td>
            <Td>{r.customer_name ?? r.account_name ?? r.tempo_account_id ?? '—'}</Td>
            <Td muted>{r.raw_status ?? '—'}</Td>
            <Td>{r.action === 'auto_approve' ? 'Approval' : 'Deposit credit'}</Td>
            <Td right>{money(r.action === 'deposit_credit' ? (r.deposit_total ?? 0) : r.total_amount)}</Td>
            <Td muted>{r.paid_date ?? r.invoice_date ?? '—'}</Td>
          </Tr>
        ))}
      </tbody>
    </Table>
  );
}

// ─── Deposit-flag backfill card ─────────────────────────────────────────────

/**
 * One-button backfill of `chargeover_invoices.has_deposit_line` for invoices
 * the bulk sync couldn't populate. ChargeOver v3's /invoice list response
 * doesn't include line_items on most tenants; the single-invoice endpoint
 * does. This card hits that single-invoice path for every open invoice
 * the flag isn't set on. Runs synchronously up to 500 invoices per
 * click; the response reports how many are still pending so the operator
 * knows whether to re-run.
 *
 * Lives on the Data sync page since it's a "data hygiene" action
 * adjacent to refresh — same mental model as "retry pending writebacks."
 */
function DepositFlagsCard() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState<{
    scanned: number; updated: number; unchanged: number;
    errored: number; remaining: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      const r = await api.admin.refreshDepositFlags(500);
      setResult(r);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Invoice line-item backfill</span>
        {/* Was labelled "deposit-line backfill / run once after the migration",
            which undersold it: the same per-invoice fetch also fills the
            services/fees split, which terminal invoices need and which the
            bulk list never returns. Run it whenever Services/Fees show as "—". */}
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
          Fills the services/fees split and the deposit flag by fetching invoices from ChargeOver
          one at a time — the bulk list omits line items, so this is the only way to get them.
          Run it repeatedly until “remaining” stops falling. It can plateau above zero: ChargeOver
          returns no line items at all for some older invoices, and those stay unsplit.
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <ActionButton onClick={run} variant="default" disabled={running}>
            {running ? 'Running…' : 'Backfill 500 invoices'}
          </ActionButton>
        </span>
      </CardHeader>
      {error && (
        <div style={{
          padding: '10px 16px', background: 'var(--color-red-bg)',
          color: 'var(--color-red-text)', fontSize: 12, borderTop: '0.5px solid var(--color-border)',
        }}>{error}</div>
      )}
      {result && (
        <div style={{
          padding: '10px 16px', borderTop: '0.5px solid var(--color-border)',
          fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap',
        }}>
          <span><strong>Scanned:</strong> {result.scanned}</span>
          <span style={{ color: 'var(--color-green-text)' }}><strong>Updated (deposit found):</strong> {result.updated}</span>
          <span style={{ color: 'var(--color-text-muted)' }}><strong>Unchanged:</strong> {result.unchanged}</span>
          {result.errored > 0 && (
            <span style={{ color: 'var(--color-red-text)' }}><strong>Errored:</strong> {result.errored}</span>
          )}
          <span style={{ marginLeft: 'auto', color: result.remaining > 0 ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
            <strong>Remaining:</strong> {result.remaining}
            {result.remaining > 0 && ' — click again to continue'}
          </span>
        </div>
      )}
    </Card>
  );
}

/**
 * Reconstruct invoice → worklog detail for invoices raised before the importer
 * started capturing it.
 *
 * Sits beside the deposit-line backfill for the same reason: a data-hygiene
 * action adjacent to refresh, and it genuinely depends on refresh — worklog
 * facts are copied at capture time, so a run before the first full sync
 * freezes NULL Jira worklog ids into every row. Hence the ordering note and
 * the separate re-run control.
 */
function InvoiceDetailBackfillCard() {
  const [running, setRunning] = useState<'normal' | 'redo' | null>(null);
  const [result, setResult] = useState<InvoiceDetailBackfillResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { data: coverage, refetch: refetchCoverage } = useQuery(
    () => api.admin.invoiceDetailCoverage(),
  );

  async function run(redo: boolean) {
    setRunning(redo ? 'redo' : 'normal');
    setError(null);
    try {
      // verify=true cross-checks against ChargeOver's stored worklog list at
      // one API call per invoice. Worth the calls: without it there is nothing
      // to corroborate a reconstruction against.
      const r = await api.admin.backfillInvoiceDetail(200, true, redo);
      setResult(r);
      refetchCoverage();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRunning(null);
    }
  }

  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Invoice worklog detail</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
          Rebuilds the worklogs behind historical invoices so /admin/invoicing can show them. New imports capture this automatically. Run a full sync first — Jira worklog links are copied at capture time and won't fill in later.
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <ActionButton onClick={() => run(false)} variant="default" disabled={running !== null}>
            {running === 'normal' ? 'Running…' : 'Backfill 200 invoices'}
          </ActionButton>
          <ActionButton onClick={() => run(true)} variant="default" disabled={running !== null}>
            {running === 'redo' ? 'Re-running…' : 'Re-run since last full sync'}
          </ActionButton>
        </span>
      </CardHeader>
      {coverage && (
        <div style={{
          padding: '10px 16px', borderTop: '0.5px solid var(--color-border)',
          fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap',
          color: 'var(--color-text-muted)',
        }}>
          <span><strong>Invoices with detail:</strong> {coverage.invoices_with_detail}</span>
          <span><strong>Worklogs:</strong> {coverage.worklogs}</span>
          <span style={{ color: 'var(--color-green-text)' }}><strong>Captured at import:</strong> {coverage.lines_imported} lines</span>
          <span style={{ color: 'var(--color-amber-text)' }}><strong>Reconstructed:</strong> {coverage.lines_reconstructed} lines</span>
          {coverage.lines_mismatched > 0 && (
            <span style={{ color: 'var(--color-red-text)' }}>
              <strong>ChargeOver disagrees:</strong> {coverage.lines_mismatched} lines
            </span>
          )}
        </div>
      )}
      {error && (
        <div style={{
          padding: '10px 16px', background: 'var(--color-red-bg)',
          color: 'var(--color-red-text)', fontSize: 12, borderTop: '0.5px solid var(--color-border)',
        }}>{error}</div>
      )}
      {result && (
        <div style={{
          padding: '10px 16px', borderTop: '0.5px solid var(--color-border)',
          fontSize: 11, display: 'flex', gap: 14, flexWrap: 'wrap',
        }}>
          <span><strong>Scanned:</strong> {result.scanned}</span>
          <span style={{ color: 'var(--color-green-text)' }}><strong>Reconstructed:</strong> {result.reconstructed}</span>
          {/* Not an error: zero-out invoices, pruned worklogs, and months older
              than the worklog sync window all legitimately land here. They are
              marked attempted so they stop occupying the page. */}
          <span style={{ color: 'var(--color-text-muted)' }}><strong>Nothing to rebuild:</strong> {result.no_worklogs}</span>
          {result.verified_match > 0 && (
            <span style={{ color: 'var(--color-green-text)' }}><strong>ChargeOver agrees:</strong> {result.verified_match}</span>
          )}
          {result.verified_mismatch > 0 && (
            <span style={{ color: 'var(--color-red-text)' }}><strong>ChargeOver disagrees:</strong> {result.verified_mismatch}</span>
          )}
          {result.verify_unavailable > 0 && (
            <span style={{ color: 'var(--color-text-muted)' }}><strong>Unverifiable:</strong> {result.verify_unavailable}</span>
          )}
          {result.errors > 0 && (
            <span style={{ color: 'var(--color-red-text)' }}><strong>Errored:</strong> {result.errors}</span>
          )}
          <span style={{ marginLeft: 'auto', color: result.remaining > 0 ? 'var(--color-amber-text)' : 'var(--color-text-muted)' }}>
            <strong>Remaining:</strong> {result.remaining}
            {result.remaining > 0 && ' — click again to continue'}
          </span>
        </div>
      )}
    </Card>
  );
}

function WritebacksCard({
  summary, draining, onDrain,
}: {
  summary: WbSummary;
  draining: boolean;
  onDrain: () => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const { data: detail, isLoading, refetch } = useQuery(
    () => open ? api.admin.writebacks.list('pending') : Promise.resolve(null),
    [open],
  );
  return (
    <Card>
      <CardHeader>
        <span style={{ fontSize: 12, fontWeight: 500 }}>Jira writebacks</span>
        <span style={{ fontSize: 11, color: 'var(--color-text-muted)', marginLeft: 8 }}>
          Shipped Date + Shipped Value pushed to Jira on period lock (spec §5.4)
        </span>
        <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
          <Badge variant={summary.pending > 0 ? 'amber' : 'green'}>
            {summary.pending} pending
          </Badge>
          <Badge variant="gray">{summary.done_today} done today</Badge>
          <ActionButton
            onClick={() => { setOpen((v) => !v); if (open) refetch(); }}
            variant="default"
            disabled={summary.pending === 0}
          >
            {open ? 'Hide pending' : 'View pending'}
          </ActionButton>
          <ActionButton
            onClick={async () => { await onDrain(); if (open) refetch(); }}
            variant="default"
            disabled={draining || summary.pending === 0}
          >
            {draining ? 'Draining…' : 'Retry pending now'}
          </ActionButton>
        </span>
      </CardHeader>
      {open && (
        <div style={{ padding: '10px 16px', maxHeight: 480, overflow: 'auto' }}>
          {isLoading && <span style={{ fontSize: 11, color: 'var(--color-text-muted)' }}>Loading…</span>}
          {detail && detail.writebacks.length === 0 && (
            <EmptyState message="No pending writebacks." />
          )}
          {detail && detail.writebacks.length > 0 && <WritebacksTable rows={detail.writebacks} />}
        </div>
      )}
    </Card>
  );
}

function WritebacksTable({ rows }: { rows: import('../lib/api').WritebackDetailRow[] }) {
  return (
    <Table>
      <thead>
        <tr>
          <Th>Issue</Th>
          <Th>Type</Th>
          <Th>Month</Th>
          <Th right>Shipped date</Th>
          <Th right>Shipped value</Th>
          <Th right>Attempts</Th>
          <Th>Last attempt</Th>
          <Th>Reason</Th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r) => {
          const neverTried = r.attempt_count === 0;
          const reason =
            r.last_error
            ?? (neverTried ? 'Never attempted — will run on the next drain.' : '—');
          return (
            <Tr key={r.id}>
              <Td bold>{r.jira_issue_key}</Td>
              <Td muted style={{ fontSize: 11 }}>{r.issue_type}</Td>
              <Td muted style={{ fontSize: 11 }}>{r.attribution_month}</Td>
              <Td right muted style={{ fontSize: 11 }}>{r.shipped_date}</Td>
              <Td right>${Number(r.shipped_value).toFixed(2)}</Td>
              <Td right style={{ color: r.attempt_count > 0 ? 'var(--color-amber)' : 'var(--color-text-muted)' }}>
                {r.attempt_count}
              </Td>
              <Td muted style={{ fontSize: 11 }}>
                {r.last_attempted_at
                  ? fmtDatetime(r.last_attempted_at)
                  : <span style={{ color: 'var(--color-text-hint)' }}>—</span>}
              </Td>
              <Td muted style={{
                fontSize: 11,
                color: r.last_error ? 'var(--color-red-text)' : 'var(--color-text-muted)',
                maxWidth: 400,
              }}>
                {reason}
              </Td>
            </Tr>
          );
        })}
      </tbody>
    </Table>
  );
}
